import { useEffect, useRef, useState } from 'react'
import type { InputHTMLAttributes } from 'react'
import { useT } from '../../i18n/context'
import { Input, Select } from './Field'

/**
 * Datum als nativer Datepicker; Uhrzeit als 24h-Auswahl in
 * 15-Minuten-Schritten. Native Zeit-Inputs (type="time" bzw.
 * "datetime-local") rendern die Anzeige je nach Browser-Locale mit
 * AM/PM und lassen sich per HTML nicht auf 24h erzwingen — daher
 * Selects für die Uhrzeit. Wert-Vertrag wie bisher: 'yyyy-mm-dd',
 * 'HH:mm' bzw. 'yyyy-MM-ddTHH:mm'.
 */

const QUARTERS = ['00', '15', '30', '45']

/** Viertelstunden; Bestand-Minuten, die kein Viertel sind, als Zusatzoption. */
function minuteOptions(mm: string): string[] {
    if (mm && !QUARTERS.includes(mm)) return [...QUARTERS, mm].sort()
    return QUARTERS
}

/** Angefangene Auswahl zu gültiger Zeit vervollständigen (Stunde → :00). */
function completeTime(hh: string, mm: string): { hh: string; mm: string } {
    return { hh: hh || (mm ? '00' : ''), mm: mm || (hh ? '00' : '') }
}

interface TimeParts {
    hh: string
    mm: string
}

function timePartsOf(value: string): TimeParts {
    const [hh = '', mm = ''] = value.split(':')
    return { hh, mm }
}

interface ControlProps {
    value: string
    onChange: (value: string) => void
    className?: string
    disabled?: boolean
}

function TimeSelectGroup({
    hh,
    mm,
    disabled,
    className = '',
    onPick,
}: {
    hh: string
    mm: string
    disabled?: boolean
    className?: string
    onPick: (hh: string, mm: string) => void
}) {
    const t = useT()
    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <Select
                aria-label={t('common.hour')}
                value={hh}
                disabled={disabled}
                onChange={e => onPick(e.target.value, mm)}
                className='w-[5rem]'
            >
                <option value=''>—</option>
                {Array.from({ length: 24 }, (_, i) =>
                    String(i).padStart(2, '0'),
                ).map(h => (
                    <option key={h} value={h}>
                        {h}
                    </option>
                ))}
            </Select>
            <span
                className='text-slate-500 dark:text-slate-400'
                aria-hidden='true'
            >
                :
            </span>
            <Select
                aria-label={t('common.minute')}
                value={mm}
                disabled={disabled}
                onChange={e => onPick(hh, e.target.value)}
                className='w-[5rem]'
            >
                <option value=''>—</option>
                {minuteOptions(mm).map(m => (
                    <option key={m} value={m}>
                        {m}
                    </option>
                ))}
            </Select>
        </div>
    )
}

type DateInputProps = Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'type' | 'value' | 'onChange'
> & {
    value: string
    onChange: (value: string) => void
    className?: string
}

/** Datumsauswahl als Datepicker — Wert 'yyyy-mm-dd' bzw. ''. */
export function DateInput({ value, onChange, className = '', ...rest }: DateInputProps) {
    return (
        <Input
            type='date'
            value={value}
            onChange={e => onChange(e.target.value)}
            className={`dark:[color-scheme:dark] ${className}`}
            {...rest}
        />
    )
}

/** Zeitauswahl Stunde:Minute (24h, 15-Min-Schritte) — Wert 'HH:mm' bzw. ''. */
export function TimeInput({ value, onChange, className, disabled }: ControlProps) {
    const [parts, setParts] = useState(() => timePartsOf(value))
    // Eigene Emissionen nicht als Außen-Änderung zurückspielen.
    const lastEmitted = useRef(value)
    useEffect(() => {
        if (value !== lastEmitted.current) {
            lastEmitted.current = value
            setParts(timePartsOf(value))
        }
    }, [value])

    const pick = (hh: string, mm: string) => {
        const next = completeTime(hh, mm)
        setParts(next)
        const emitted = next.hh && next.mm ? `${next.hh}:${next.mm}` : ''
        lastEmitted.current = emitted
        onChange(emitted)
    }

    return (
        <TimeSelectGroup
            hh={parts.hh}
            mm={parts.mm}
            disabled={disabled}
            className={className}
            onPick={pick}
        />
    )
}

/** Datum (Datepicker) + Uhrzeit — Wert 'yyyy-MM-ddTHH:mm' bzw. ''. */
export function DateTimeInput({ value, onChange, className = '', disabled }: ControlProps) {
    const [state, setState] = useState(() => {
        const [d = '', time = ''] = value.split('T')
        return { d, ...timePartsOf(time) }
    })
    const lastEmitted = useRef(value)
    useEffect(() => {
        if (value !== lastEmitted.current) {
            lastEmitted.current = value
            const [d = '', time = ''] = value.split('T')
            setState({ d, ...timePartsOf(time) })
        }
    }, [value])

    const emit = (d: string, hh: string, mm: string) => {
        const time = completeTime(hh, mm)
        setState({ d, ...time })
        const emitted =
            d && time.hh && time.mm ? `${d}T${time.hh}:${time.mm}` : ''
        lastEmitted.current = emitted
        onChange(emitted)
    }

    return (
        <div className={`flex flex-wrap items-center gap-2 ${className}`}>
            <Input
                type='date'
                value={state.d}
                disabled={disabled}
                onChange={e => emit(e.target.value, state.hh, state.mm)}
                className='dark:[color-scheme:dark] min-w-[9.5rem] flex-1'
            />
            <TimeSelectGroup
                hh={state.hh}
                mm={state.mm}
                disabled={disabled}
                onPick={(hh, mm) => emit(state.d, hh, mm)}
            />
        </div>
    )
}
